import { datadogLogs } from "@datadog/browser-logs";
import { stableStringifyJson } from "@redotech/json/serialize";
import { useLoad } from "@redotech/react-util/load";
import type { RpcClient } from "@redotech/rpc/client";
import type { RpcClientDefinition } from "@redotech/rpc/definition";
import { sleep } from "@redotech/util/schedule";
import { useRef, useState } from "react";
import { BehaviorSubject } from "rxjs";

type UnaryStreamMethods<C> = {
  [K in keyof C]: C[K] extends { method: "unary-stream" } ? K : never;
}[keyof C];

type StreamOutputType<T> = T extends (
  ...args: any[]
) => AsyncIterableIterator<infer U>
  ? U
  : never;

export class CloseStream extends Error {
  readonly errorCode = "CLOSE_STREAM";
  constructor() {
    super("Stream closed");
  }
}

export interface UseWatchStreamOptions {
  disableCompression?: boolean;
}

export function useWatchStream<
  C extends RpcClient<RpcClientDefinition>,
  K extends UnaryStreamMethods<C>,
>(
  client: C | undefined,
  method: K,
  input: Parameters<C[K]>[0],
  fn: (response: StreamOutputType<C[K]>) => void,
  skipInTesting: boolean,
  options?: UseWatchStreamOptions,
): boolean {
  const [loading, setLoading] = useState(false);

  useLoad(
    async (signal) => {
      // Skip streaming in tests
      if (
        !client ||
        (skipInTesting &&
          (process.env.NODE_ENV === "test" ||
            process.env.TEST_TYPE === "e2e_test"))
      ) {
        return;
      }
      setLoading(true);

      let errorCount = 0;
      while (true) {
        if (signal.aborted) {
          break;
        }
        try {
          // Backoff if we're getting back to back errors
          await sleep(
            Temporal.Duration.from({
              milliseconds: Math.min(
                Math.floor(1000 * 2 ** errorCount + Math.random() * 1000),
                30_000,
              ),
            }),
            signal,
          );

          let logged = false;
          const start = performance.now();

          const requestHeaders: Record<string, string> = {};
          if (options?.disableCompression) {
            requestHeaders["Accept-Encoding"] = "identity";
          }

          const iterator = client[method](input, {
            signal,
            requestHeaders,
          }) as AsyncIterable<StreamOutputType<C[K]>>;

          for await (const response of iterator) {
            if (signal.aborted) {
              break;
            }
            if (!logged) {
              setLoading(false);
              logged = true;
              datadogLogs.logger.info("initial stream response", {
                duration: performance.now() - start,
              });
            }

            fn(response);
            errorCount = 0;
          }
        } catch (e) {
          if (signal.aborted || e instanceof CloseStream) {
            break;
          }
          errorCount++;
          console.error("Error while watching for updates", e);
        }
      }
    },
    [client, method, setLoading, stableStringifyJson(input)],
  );

  return loading;
}

export function useObserveStream<
  C extends RpcClient<RpcClientDefinition>,
  K extends UnaryStreamMethods<C>,
>(
  client: C | undefined,
  method: K,
  input: Parameters<C[K]>[0],
  initialValue: StreamOutputType<C[K]>,
  fn?: (
    oldValue: StreamOutputType<C[K]>,
    newValue: StreamOutputType<C[K]>,
  ) => void,
  options?: UseWatchStreamOptions,
) {
  const subject = useRef(
    new BehaviorSubject<StreamOutputType<C[K]>>(initialValue),
  );

  const skipStreamInTesting = true;
  useWatchStream(
    client,
    method,
    input,
    (response) => {
      if (fn) {
        fn(subject.current.getValue(), response);
      }
      subject.current.next(response);
    },
    skipStreamInTesting,
    options,
  );

  return subject.current;
}
